import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Table, Button, Space, Tag, Switch, Input, Select, Popconfirm, App, Tooltip, Flex } from 'antd';
import { PlusOutlined, ImportOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import ServerFormModal from '../components/ServerFormModal';
import ImportModal from '../components/ImportModal';
import {
  HealthServer,
  ItemsCell,
  ProxyConfig,
  ServerRowDetail,
  StatusDot,
  networkItems,
  proxyItems,
} from '../components/ServerListCells';

const { Title, Text } = Typography;

interface Server {
  key: string;
  name: string;
  mainIP: string;
  networks: string[];
  proxies: ProxyConfig[];
  checkMode: string;
  type?: 'dedicated' | 'mobile-gateway';
  country: string;
  tags: string[];
  enabled: boolean;
}

const MODE_LABELS: Record<string, string> = {
  'active-probing': 'Active Probing',
  'agent-heartbeat': 'Agent Heartbeat',
};

// The list is read on a 1280px-wide laptop: every column that is not the
// server's identity is sized to its content and nothing more.
const ServersPage: React.FC = () => {
  const { message } = App.useApp();
  const [servers, setServers] = useState<Server[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<string>('');
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editServer, setEditServer] = useState<Server | null>(null);
  const [healthMap, setHealthMap] = useState<Record<string, HealthServer>>({});
  const [tagFilter, setTagFilter] = useState('');
  const [countryOptions, setCountryOptions] = useState<{ label: string; value: string }[]>([]);
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    apiClient.get('/servers/filters').then(({ data }) => {
      if (data.data?.countries) {
        setCountryOptions(data.data.countries.map((c: string) => ({ label: c, value: c })));
      }
      if (data.data?.tags) {
        setTagOptions(data.data.tags.map((t: string) => ({ label: t, value: t })));
      }
    }).catch(() => {});
  }, []);

  const fetchServers = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: pageSize };
      if (search) params.search = search;
      if (countryFilter) params.country = countryFilter;
      if (modeFilter) params.checkMode = modeFilter;
      if (typeFilter) params.type = typeFilter;
      if (enabledFilter) params.enabled = enabledFilter;
      if (tagFilter) params.tag = tagFilter;

      const [serversRes, healthRes] = await Promise.all([
        apiClient.get('/servers', { params }),
        apiClient.get('/health/servers', { params }).catch(() => null),
      ]);

      setServers(serversRes.data.data.items || []);
      setTotal(serversRes.data.data.total || 0);

      if (healthRes?.data?.data?.items) {
        const map: Record<string, HealthServer> = {};
        for (const h of healthRes.data.data.items) {
          map[h.key] = h;
        }
        setHealthMap(map);
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load servers');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, countryFilter, modeFilter, typeFilter, enabledFilter, tagFilter, message]);

  useEffect(() => {
    fetchServers();
  }, [fetchServers]);

  const handleDelete = async (key: string) => {
    try {
      await apiClient.delete(`/servers/${key}`);
      message.success('Server deleted');
      fetchServers();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleToggleEnabled = async (server: Server) => {
    try {
      await apiClient.put(`/servers/${server.key}`, { enabled: !server.enabled });
      fetchServers();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Update failed');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (name: string, record: Server) => (
        <Tooltip title={name} placement="topLeft">
          <Link to={`/servers/${record.key}`}>{name}</Link>
        </Tooltip>
      ),
    },
    {
      // The main IP with its status dot; the server's other networks fold into
      // a "+N" chip beside it (spelled out on hover and in the expanded row).
      // They used to be a column of their own that read "--" on almost every
      // row and cost 140px the table does not have at 1280px.
      title: 'Main IP',
      dataIndex: 'mainIP',
      key: 'mainIP',
      width: 150,
      render: (ip: string, record: Server) => {
        const state = healthMap[record.key]?.networkStatuses?.find((s) => s.ip === ip)?.state;
        const others = networkItems(record.networks, healthMap[record.key], record.mainIP);
        return (
          <Flex align="center" gap={4} style={{ minWidth: 0, whiteSpace: 'nowrap' }}>
            {state && <StatusDot state={state} />}
            <span>{ip}</span>
            {others.length > 0 && (
              <Tooltip
                title={others.map((n) => (
                  <div key={n.id} style={{ fontSize: 12 }}>{n.full}</div>
                ))}
              >
                <Tag style={{ margin: 0, fontSize: 12, lineHeight: '18px', padding: '0 5px' }}>
                  +{others.length} net
                </Tag>
              </Tooltip>
            )}
          </Flex>
        );
      },
    },
    {
      title: 'Proxies',
      key: 'proxies',
      width: 180,
      render: (_: any, record: Server) => (
        <ItemsCell items={proxyItems(record.proxies, healthMap[record.key], record.mainIP)} max={1} />
      ),
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 100,
      render: (tags: string[]) => (
        <ItemsCell
          items={(tags || []).map((t) => ({ id: t, text: t, full: t, state: '' }))}
          max={1}
        />
      ),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 84,
      render: (code: string, record: Server) => {
        // Prefer country from health data (Redis) when server record hasn't been updated yet.
        const resolved = code || healthMap[record.key]?.country || '';
        if (!resolved) return <Text type="secondary">--</Text>;
        return (
          <Tooltip title={resolved}>
            <img
              src={`https://flagcdn.com/20x15/${resolved.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/40x30/${resolved.toLowerCase()}.png 2x`}
              width={18}
              height={13}
              alt={resolved}
              style={{ verticalAlign: 'middle', marginRight: 4 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span style={{ verticalAlign: 'middle', fontSize: 12 }}>{resolved}</span>
          </Tooltip>
        );
      },
    },
    {
      // Two one-word facts that were two full columns; the long form of each is
      // one hover away. Plain text, not tags: neither is a status, and bordered
      // chips here read as buttons.
      title: 'Mode / Type',
      key: 'modeType',
      width: 130,
      render: (_: any, record: Server) => {
        const isMobile = record.type === 'mobile-gateway';
        return (
          <Flex align="baseline" gap={6} style={{ whiteSpace: 'nowrap' }}>
            <Tooltip title={MODE_LABELS[record.checkMode] || record.checkMode}>
              <span>
                {record.checkMode === 'active-probing' ? 'Probe' : record.checkMode === 'agent-heartbeat' ? 'Agent' : record.checkMode}
              </span>
            </Tooltip>
            <Tooltip title={isMobile ? 'Mobile gateway — proxies tunnel through independent modems' : 'Dedicated server'}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {isMobile ? 'Mobile' : 'Dedicated'}
              </Text>
            </Tooltip>
          </Flex>
        );
      },
    },
    {
      title: 'On',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 56,
      render: (_: boolean, record: Server) => (
        <Tooltip title={record.enabled ? 'Enabled — checks are scheduled' : 'Disabled — no checks'}>
          <Switch
            checked={record.enabled}
            size="small"
            aria-label={record.enabled ? `Disable ${record.name}` : `Enable ${record.name}`}
            onChange={() => handleToggleEnabled(record)}
          />
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 64,
      render: (_: any, record: Server) => (
        <Space size={0}>
          <Tooltip title="Edit server">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              aria-label={`Edit ${record.name}`}
              onClick={() => { setEditServer(record); setFormOpen(true); }}
            />
          </Tooltip>
          <Popconfirm title={`Delete ${record.name}?`} onConfirm={() => handleDelete(record.key)}>
            <Tooltip title="Delete server">
              <Button type="text" danger icon={<DeleteOutlined />} size="small" aria-label={`Delete ${record.name}`} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 8, justifyContent: 'space-between', width: '100%' }}>
        <Space align="baseline" size={8}>
          <Title level={4} style={{ margin: 0 }}>Servers</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>{total}</Text>
        </Space>
        <Space size={8}>
          <Button size="small" icon={<ImportOutlined />} onClick={() => setImportOpen(true)}>Import</Button>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => { setEditServer(null); setFormOpen(true); }}>
            Add Server
          </Button>
        </Space>
      </Space>

      <Space style={{ marginBottom: 8 }} size={8} wrap>
        <Input.Search
          size="small"
          placeholder="Name, IP, proxy host:port, modem…"
          allowClear
          onSearch={(v) => { setSearch(v); setPage(1); }}
          style={{ width: 210 }}
        />
        <Select
          size="small"
          placeholder="Country"
          allowClear
          showSearch
          style={{ width: 110 }}
          onChange={(v) => { setCountryFilter(v || ''); setPage(1); }}
          options={countryOptions}
        />
        <Select
          size="small"
          placeholder="Mode"
          allowClear
          style={{ width: 120 }}
          onChange={(v) => { setModeFilter(v || ''); setPage(1); }}
          options={[
            { label: 'Active Probing', value: 'active-probing' },
            { label: 'Agent Heartbeat', value: 'agent-heartbeat' },
          ]}
        />
        <Select
          size="small"
          placeholder="Type"
          allowClear
          style={{ width: 130 }}
          onChange={(v) => { setTypeFilter(v || ''); setPage(1); }}
          options={[
            { label: 'Dedicated', value: 'dedicated' },
            { label: 'Mobile gateway', value: 'mobile-gateway' },
          ]}
        />
        <Select
          size="small"
          placeholder="Enabled"
          allowClear
          style={{ width: 100 }}
          onChange={(v) => { setEnabledFilter(v || ''); setPage(1); }}
          options={[
            { label: 'Enabled', value: 'true' },
            { label: 'Disabled', value: 'false' },
          ]}
        />
        <Select
          size="small"
          placeholder="Tag"
          allowClear
          showSearch
          style={{ width: 110 }}
          onChange={(v) => { setTagFilter(v || ''); setPage(1); }}
          options={tagOptions}
        />
      </Space>

      <Table
        dataSource={servers}
        columns={columns}
        rowKey="key"
        size="small"
        loading={loading}
        expandable={{
          columnWidth: 32,
          // Everything the one-line cells folded away: every network, proxy and
          // tag of the row, spelled out.
          expandedRowRender: (record: Server) => (
            <ServerRowDetail
              networks={networkItems(record.networks, healthMap[record.key], record.mainIP)}
              proxies={proxyItems(record.proxies, healthMap[record.key], record.mainIP)}
              tags={record.tags || []}
            />
          ),
          rowExpandable: (record: Server) =>
            (record.networks || []).length > 0
            || (record.proxies || []).length > 0
            || (record.tags || []).length > 0
            || !!healthMap[record.key],
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          size: 'small',
          showSizeChanger: true,
          showTotal: (t, range) => `${range[0]}–${range[1]} of ${t}`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
      />

      <ServerFormModal
        open={formOpen}
        server={editServer}
        onCancel={() => setFormOpen(false)}
        onSuccess={() => { setFormOpen(false); fetchServers(); }}
      />

      <ImportModal
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        onSuccess={() => fetchServers()}
      />
    </>
  );
};

export default ServersPage;
